/**
 * lx-contact-list —— 联系人列表（含字母索引条）
 *
 * 交互对齐手绘草图与移动端惯例：
 *   - 按拼音首字母分组，每组一个小标题
 *   - 右侧字母索引条，点击跳转
 *   - 点击条目进入会话
 *
 * 订阅 contact:list-changed 自动重载 —— 所以无论联系人是从表单建的、
 * 还是 AI 用 IL 建的，列表都会同步更新，不需要任何额外通知。
 */

import { LxElement, define } from '@/core/base-element';
import { contactsRepo } from '@/data/repo.contacts';
import { sessionsRepo } from '@/data/repo.sessions';
import { firstLetter } from '@/data/pinyin';
import { pushOverlay, uiStore } from '@/core/ui-store';
import type { Contact } from '@/types/models';

interface Group {
  letter: string;
  contacts: Contact[];
}

export class LxContactList extends LxElement {
  private _contacts: Contact[] = [];
  private _loading = true;

  protected styles(): string {
    return `
      :host {
        display: block;
        width: 100%;
        position: relative;
      }

      .loading {
        padding: var(--sp-6);
        text-align: center;
        color: var(--c-fg-subtle);
        font-size: var(--fs-sm);
      }

      .groups { padding-bottom: var(--sp-5); }

      .group-title {
        padding: var(--sp-2) var(--layout-content-pad-x) var(--sp-1);
        font-size: var(--fs-xs);
        color: var(--c-fg-muted);
        letter-spacing: 0.08em;
        position: sticky;
        top: 0;
        background: var(--c-bg);
        z-index: 1;
      }

      .row {
        display: flex;
        align-items: center;
        gap: var(--sp-3);
        width: 100%;
        padding: var(--sp-2) var(--layout-content-pad-x);
        min-height: 56px;
        text-align: left;
        transition: background var(--dur-fast) var(--ease-standard);
      }

      .row:hover { background: var(--c-surface-2); }
      .row:active { background: var(--c-surface-3, var(--c-surface-2)); }

      .avatar {
        flex-shrink: 0;
        width: 40px;
        height: 40px;
        border-radius: var(--radius-full);
        display: grid;
        place-items: center;
        font-size: var(--fs-md);
        font-weight: var(--fw-medium);
        /* 同会话列表：中性底，把主色留给真正的信号 */
        color: var(--c-fg-muted);
        background: var(--c-surface-2);
        user-select: none;
      }

      .meta {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 2px;
      }

      .name-line {
        display: flex;
        align-items: baseline;
        gap: var(--sp-2);
        min-width: 0;
      }

      .name {
        font-size: var(--fs-md);
        color: var(--c-fg);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .remark {
        font-size: var(--fs-xs);
        color: var(--c-fg-subtle);
        flex-shrink: 0;
      }

      .signature {
        font-size: var(--fs-xs);
        color: var(--c-fg-muted);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      /* ---------- 右侧字母索引条 ---------- */
      /* 绝对定位相对 :host（它不是滚动容器），因此索引条不会跟着内容滚走。
         用 sticky + float 会和滚动容器打架，这里避开。 */
      .index {
        position: absolute;
        right: 2px;
        top: 50%;
        transform: translateY(-50%);
        display: flex;
        flex-direction: column;
        gap: 1px;
        padding: var(--sp-1) 2px;
        z-index: 2;
      }

      .index button {
        font-size: 10px;
        line-height: 1;
        width: 16px;
        height: 13px;
        display: grid;
        place-items: center;
        color: var(--c-fg-subtle);
        border-radius: var(--radius-sm);
        transition: color var(--dur-fast) var(--ease-standard);
      }

      .index button:hover { color: var(--c-fg); background: var(--c-surface-2); }
    `;
  }

  protected render(): string {
    if (this._loading) return '<p class="loading">正在读取</p>';
    if (this._contacts.length === 0) return ''; // 空态由父组件渲染

    const groups = this.group();
    const letters = groups.map((g) => g.letter);

    return `
      <div class="groups">
        ${groups
          .map(
            (g) => `
          <div class="group" data-letter="${g.letter}">
            <h3 class="group-title">${g.letter}</h3>
            ${g.contacts.map((c) => this.renderRow(c)).join('')}
          </div>
        `,
          )
          .join('')}
      </div>

      ${
        letters.length > 1
          ? `<nav class="index" aria-label="字母索引">
              ${letters
                .map((l) => `<button type="button" data-jump="${l}" aria-label="跳到 ${l}">${l}</button>`)
                .join('')}
            </nav>`
          : ''
      }
    `;
  }

  private renderRow(c: Contact): string {
    // 有备注名时显示备注，原姓名作为小字提示
    const displayName = c.remark || c.name;
    const initial = displayName.charAt(0);

    return `
      <button class="row" type="button" data-id="${c.id}">
        <span class="avatar">${this.esc(initial.toUpperCase())}</span>
        <span class="meta">
          <span class="name-line">
            <span class="name">${this.esc(displayName)}</span>
            ${c.remark ? `<span class="remark">${this.esc(c.name)}</span>` : ''}
          </span>
          ${c.signature ? `<span class="signature">${this.esc(c.signature)}</span>` : ''}
        </span>
      </button>
    `;
  }

  private group(): Group[] {
    const map = new Map<string, Contact[]>();
    for (const c of this._contacts) {
      // 用备注名（若存在）参与分组，因为它才是列表上显示的名字
      const letter = firstLetter(c.remark || c.name);
      const arr = map.get(letter) ?? [];
      arr.push(c);
      map.set(letter, arr);
    }

    // '#' 组永远排在最后
    return Array.from(map.entries())
      .map(([letter, contacts]) => ({ letter, contacts }))
      .sort((a, b) => {
        if (a.letter === '#') return 1;
        if (b.letter === '#') return -1;
        return a.letter.localeCompare(b.letter);
      });
  }

  protected onMounted(): void {
    this.$$('.row').forEach((row) => {
      row.addEventListener('click', () => {
        const id = row.getAttribute('data-id');
        if (id) void this.openSession(id);
      });
    });

    this.$$('[data-jump]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const letter = btn.getAttribute('data-jump');
        const target = this.$(`[data-letter="${letter}"]`);
        target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  private async openSession(contactId: string): Promise<void> {
    const session = await sessionsRepo.ensureForContact(contactId);
    pushOverlay({ kind: 'chat', refId: session.id });
    uiStore.set({});
  }

  private async load(): Promise<void> {
    this._contacts = await contactsRepo.list();
    this._loading = false;
    this.update();
  }

  connectedCallback(): void {
    super.connectedCallback();
    void this.load();
    this.subscribe('contact:list-changed', () => void this.load());
  }
}

define('lx-contact-list', LxContactList);
